import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CampaignIcon from "@mui/icons-material/Campaign";
import PersonIcon from "@mui/icons-material/Person";
import Loader from "../../utils/Loading";
import { CandidateLeadsDashboard } from "../../utils/CandidateDashboard";
import { LEAD_STATUS_OPTIONS } from "../../utils/campaignLeadHelpers";
import { fetchCampaignLeadsPage } from "../../utils/fetchCampaignLeads";
import PaginationComponent from "../../utils/PaginationComponent";
import { roleBasedRedirection } from "../../utils/helper";
import { getCurrentSubscription } from "../../helper/subscriptionHelper";
import CampaignLeadCard from "../../components/CampaignLeads/CampaignLeadCard";
import "../../styles/Listing/listing-premium.css";

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState<CandidateLeadsDashboard>();
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const location = useLocation();
  const { campaignID } = (location.state as { campaignID?: string }) || {};

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const viewProfile = (id: number) =>
    navigate(roleBasedRedirection("/candidate-leads-dashboard/view"), {
      state: { id, filter },
    });

  const loadLeads = async (
    pageIndex: number,
    limit: number,
    status: string
  ) => {
    if (!campaignID) return;

    setLoading(true);
    const result = await fetchCampaignLeadsPage({
      campaignId: campaignID,
      page: pageIndex + 1,
      limit,
      status: status || undefined,
    });

    if (result) {
      setDashboardData({
        success: true,
        statusCode: 200,
        statusMessage: "",
        data: {
          candidate: result.candidates,
          count: result.count,
          page: result.page + 1,
          name: result.name,
          campaignName: result.name,
          totalPages: Math.ceil(result.count / limit),
          hasNextPage: result.hasNextPage,
          hasPreviousPage: result.hasPreviousPage,
        },
      });
      setCount(result.count);
      setPage(result.page);
      setHasNextPage(result.hasNextPage);
      setHasPreviousPage(result.hasPreviousPage);
    }
    setLoading(false);
  };

  const handleFilterChange = async (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setFilter(value);
    setPage(0);
    await loadLeads(0, rowsPerPage, value);
  };

  const handleCallToAction = async (id: number) => {
    try {
      const { isActive, subscription } = await getCurrentSubscription();
      if (!isActive) {
        navigate("/subscriptions");
        return;
      }
      if (subscription?.type?.toLowerCase()?.trim() === "basic") {
        navigate("/update-subscriptions");
        return;
      }
      viewProfile(id);
    } catch {
      navigate("/subscriptions");
    }
  };

  useEffect(() => {
    if (campaignID) {
      loadLeads(page, rowsPerPage, filter);
    }
  }, [page, rowsPerPage, campaignID]);

  const candidates = dashboardData?.data?.candidate || [];

  return (
    <>
      <Box className="listing-page">
        <Box className="listing-hero">
          <Box className="listing-hero-inner">
            <Box>
              <Box className="listing-hero-badge">
                <CampaignIcon sx={{ fontSize: 14 }} />
                Campaign Leads
              </Box>
              <h1 className="listing-hero-title">
                {dashboardData?.data?.name || "Loading..."}
              </h1>
              <p className="listing-hero-subtitle">
                Manage and review all candidate leads
              </p>
            </Box>

            <Box className="listing-hero-filter">
              <FilterListIcon sx={{ color: "#fff", fontSize: 20 }} />
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Status</InputLabel>
                <Select
                  value={filter}
                  onChange={handleFilterChange}
                  label="Filter by Status"
                >
                  {LEAD_STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value || "all"} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>

        {loading ? (
          <Loader />
        ) : candidates.length > 0 ? (
          <>
            <Box className="listing-list">
              {candidates.map((data, index) => (
                <CampaignLeadCard
                  key={data.id || index}
                  lead={data}
                  onCallToAction={handleCallToAction}
                />
              ))}
            </Box>

            <Box className="listing-pagination-wrap">
              <PaginationComponent
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                count={count}
                hasNextPage={hasNextPage}
                hasPreviousPage={hasPreviousPage}
              />
            </Box>
          </>
        ) : (
          <Box className="listing-empty">
            <Box className="listing-empty-icon">
              <PersonIcon />
            </Box>
            <h2 className="listing-empty-title">No Candidates Found</h2>
            <p className="listing-empty-text">
              {filter
                ? "No candidates found with the selected filter. Try changing your filter options."
                : "No candidates have been added to this campaign yet."}
            </p>
          </Box>
        )}
      </Box>
    </>
  );
};

export default Dashboard;
